<script setup lang="ts">
/**
 * PwaUpdatePrompt — the update line. It says where the update is, and only
 * what is true: found, downloading n of m files, ready, restarting, updated to
 * <build>, or what went wrong with a way to try again. The lifecycle itself
 * lives in composables/useAppUpdate.ts, which carries the diagnosis of why the
 * app used to need a force-quit (job #759).
 *
 * Held back while a cycle is actively playing (B6/Gap 4, Tom's rule since
 * 2026-05-21) — rounds are ~11s apart, so it surfaces at the very next pause.
 * "Later" hides a ready update; the logo blue dot (LearningPlayer) takes over
 * and triggers the same restart. While the on-open screen is holding, that
 * screen shows the same stages and this line stays out of its way.
 *
 * It is also the ONE status pill (job #831): App.vue hands it the loading line
 * while the player's code is on its way, and a loading line REPLACES a passing
 * version note rather than stacking a second banner on it. Version notes fade
 * after two seconds; a failure stays until tapped, because it must be read.
 */
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useI18n } from '../composables/useI18n'
import { userDismissed } from '@/composables/usePwaUpdate'
import {
  updatePhase, updateProgress, updateTarget, updateFailure,
  applyUpdate, finishRestart, dismissUpdate, retryUpdate,
} from '@/composables/useAppUpdate'
import { updateHolding } from '@/composables/useOpenUpdateGate'
import { updateLine, shortBuild } from '@/composables/updateLine'
import { barsMounted } from '@/nav/learnerBar'

const props = defineProps<{
  /** A loading line (App.vue, while the player's code is on its way). */
  loading?: string
}>()

const { t } = useI18n()

const isPlaying = ref(false)
function onPlayState(e: Event) {
  isPlaying.value = !!(e as CustomEvent).detail?.playing
}

// Became ready while something was sounding: when the line does appear, it
// says it waited for the pause rather than pretending it just happened.
const heldForPause = ref(false)
watch(updatePhase, (p) => {
  if (p === 'ready' && isPlaying.value) heldForPause.value = true
  if (p !== 'ready') heldForPause.value = false
})

const line = computed(() => updateLine(t, {
  phase: updatePhase.value,
  progress: updateProgress.value,
  target: updateTarget.value,
  failure: updateFailure.value,
  heldForPause: heldForPause.value,
}))

// A version note — "Updated", "You have the latest version" — is a passing
// remark, not news: it shows for NOTE_MS and fades, whatever the update
// lifecycle's own notice timer says (Settings reads that one for longer).
const NOTE_MS = 2_000
const noteDone = ref(false)
let noteTimer: ReturnType<typeof setTimeout> | null = null
const isNote = computed(() => updatePhase.value === 'updated' || updatePhase.value === 'current')
watch(updatePhase, () => {
  if (noteTimer) { clearTimeout(noteTimer); noteTimer = null }
  noteDone.value = false
  if (isNote.value) noteTimer = setTimeout(() => { noteDone.value = true }, NOTE_MS)
}, { immediate: true })

// The note's build id sits in a smaller, lighter face than its words.
const noteParts = computed(() => {
  const key = updatePhase.value === 'updated' ? 'update.updated' : 'update.current'
  const [before, after = ''] = t(key).split('{build}')
  return { before: before.trim(), build: shortBuild(updateTarget.value), after: after.trim() }
})

/** Phases that need the learner: they outrank a loading line. */
const needsYou = computed(() => {
  const p = updatePhase.value
  if (updateHolding.value) return false
  // Restarting and wedged stay visible whatever else is going on: a wedged
  // reload is only escaped by a tap.
  if (p === 'restarting' || p === 'wedged') return true
  if (isPlaying.value) return false
  if (p === 'failed') return true
  return p === 'ready' && !userDismissed.value
})

const showUpdate = computed(() => {
  const p = updatePhase.value
  if (p === 'idle' || updateHolding.value) return false
  if (needsYou.value) return true
  if (isPlaying.value || p === 'ready') return false
  if (isNote.value && noteDone.value) return false
  return true
})

/**
 * ONE pill, one message. A loading line REPLACES a passing update note rather
 * than stacking on it; only a stage that needs the learner (ready, failed,
 * restarting, wedged) takes the pill from the loading line.
 */
const mode = computed<'update' | 'loading' | null>(() => {
  if (needsYou.value) return 'update'
  if (props.loading) return 'loading'
  return showUpdate.value ? 'update' : null
})

onMounted(() => {
  window.addEventListener('ssi-play-state', onPlayState)
})

onUnmounted(() => {
  window.removeEventListener('ssi-play-state', onPlayState)
  if (noteTimer) clearTimeout(noteTimer)
})
</script>

<template>
  <!-- Teleport to body so the pill lives in the root stacking context: a
       parent with transform/filter/opacity could otherwise trap it below
       floating UI like the mode-tray trigger. -->
  <Teleport to="body">
    <Transition name="pill-fade">
      <div
        v-if="mode"
        class="status-pill"
        :class="{ 'status-pill--by-bar': barsMounted }"
        role="status"
        aria-live="polite"
        :data-update-phase="mode === 'update' ? updatePhase : 'loading'"
      >
        <div class="status-pill__body" :class="{ 'status-pill__body--alert': mode === 'update' && updatePhase === 'failed' }">
          <span class="status-pill__text">
            <span class="status-pill__words">
              <span v-if="mode === 'loading' || line.busy" class="status-pill__pulse" aria-hidden="true"></span>
              <template v-if="mode === 'loading'">{{ loading }}</template>
              <template v-else-if="isNote">
                {{ noteParts.before }}
                <span class="status-pill__build">{{ noteParts.build }}</span>
                <template v-if="noteParts.after">{{ noteParts.after }}</template>
              </template>
              <template v-else>{{ line.text }}</template>
            </span>
            <span v-if="mode === 'update' && line.fraction !== null" class="status-pill__bar" aria-hidden="true">
              <span class="status-pill__bar-fill" :style="{ width: `${Math.round(line.fraction * 100)}%` }"></span>
            </span>
          </span>
          <template v-if="mode === 'update'">
            <div v-if="updatePhase === 'ready'" class="status-pill__actions">
              <button class="status-pill__quiet" @click.stop="dismissUpdate">
                {{ t('update.later') }}
              </button>
              <button class="status-pill__action" @click.stop="applyUpdate">
                {{ t('update.restartNow') }}
              </button>
            </div>
            <div v-else-if="updatePhase === 'wedged'" class="status-pill__actions">
              <button class="status-pill__action" @click.stop="finishRestart">
                {{ t('update.restartNow') }}
              </button>
            </div>
            <div v-else-if="updatePhase === 'failed'" class="status-pill__actions">
              <button class="status-pill__quiet" @click.stop="dismissUpdate">
                {{ t('update.later') }}
              </button>
              <button class="status-pill__action" @click.stop="retryUpdate">
                {{ t('update.tryAgain') }}
              </button>
            </div>
          </template>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
/* One quiet pill in the Mist palette (job #831). Top centre, one row BELOW
   the shell's Back pill (AppEscape: 36px tall at max(12px, safe-top)), so it
   can never sit over Back or anything else in that top row; the bottom band
   is the mode tray's territory (B6). */
.status-pill {
  position: fixed;
  top: calc(var(--top-bands-h, 0px) + max(12px, env(safe-area-inset-top, 0px)) + var(--app-escape-clearance, 48px));
  left: max(16px, env(safe-area-inset-left, 0px));
  right: max(16px, env(safe-area-inset-right, 0px));
  display: flex;
  justify-content: center;
  /* Max int32 — an update the learner must act on stays reachable over any
     overlay. The row itself lets taps through; only the pill takes them. */
  z-index: 2147483647;
  pointer-events: none;
}

/* With the bottom bar on screen the pill leaves the top altogether (job #48,
   Tom 2026-09-24): up there it sat over page headers — the Listening tabs,
   the first pod card, Script's counter. It takes the mode button's row
   instead, the strip left of the green button, which every page already keeps
   clear of its own controls because the button floats there. Bottoms align
   with the button; a wrapped pill grows upward, never across it. */
.status-pill--by-bar {
  top: auto;
  bottom: var(--float-row-bottom, calc(var(--shell-nav-clearance, 12px) + 84px));
  left: var(--float-bar-left, 1rem);
  right: calc(var(--float-col-right, 32px) + var(--mode-btn-size, 44px) + 12px);
  justify-content: flex-start;
  align-items: flex-end;
}

.status-pill__body {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  max-width: 100%;
  min-height: 32px;
  padding: 6px 14px;
  /* Round as a pill on one line, and still soft when a failure wraps. */
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.82);
  backdrop-filter: blur(12px) saturate(1.4);
  -webkit-backdrop-filter: blur(12px) saturate(1.4);
  border: 1px solid var(--border-subtle, rgba(0, 0, 0, 0.10));
  box-shadow: var(--shadow-sm, 0 1px 3px rgba(44, 38, 34, 0.10), 0 4px 12px rgba(44, 38, 34, 0.07));
  color: var(--text-secondary, #4A4440);
  pointer-events: auto;
}

/* A failure has to be read, so it earns a little more ink — still the
   app's own warm palette, never a dark bar. */
.status-pill__body--alert {
  color: var(--text-primary, #2C2622);
  border-color: var(--border-strong, rgba(0, 0, 0, 0.20));
}

.status-pill__text {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.3;
}

.status-pill__words {
  display: flex;
  align-items: center;
  gap: 6px;
}

.status-pill__build {
  font-family: var(--font-mono, ui-monospace, "SF Mono", Menlo, monospace);
  font-size: 11px;
  font-weight: 400;
  color: var(--text-muted, #8A8078);
}

/* A pulse, not a spinner: it only shows while something is genuinely in
   motion, and the words beside it say what. */
.status-pill__pulse {
  flex: none;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--text-muted, #8A8078);
  animation: status-pill-pulse 1.2s ease-in-out infinite;
}

@keyframes status-pill-pulse {
  0%, 100% { opacity: 0.3; }
  50% { opacity: 1; }
}

.status-pill__bar {
  display: block;
  height: 3px;
  border-radius: 2px;
  background: var(--bg-secondary, #e0dbd5);
  overflow: hidden;
}

.status-pill__bar-fill {
  display: block;
  height: 100%;
  background: var(--text-muted, #8A8078);
  transition: width 0.3s ease;
}

.status-pill__actions {
  display: flex;
  gap: 6px;
  flex: none;
}

.status-pill__quiet,
.status-pill__action {
  min-height: 28px;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 13px;
  cursor: pointer;
  transition: background 0.15s ease;
}

.status-pill__quiet {
  background: transparent;
  border: 1px solid var(--border-subtle, rgba(0, 0, 0, 0.10));
  color: var(--text-muted, #8A8078);
}

.status-pill__action {
  background: var(--text-primary, #2C2622);
  border: none;
  color: var(--text-inverse, #fff);
  font-weight: 600;
}

.pill-fade-enter-active,
.pill-fade-leave-active {
  transition: opacity 0.35s ease, transform 0.35s ease;
}

.pill-fade-enter-from,
.pill-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
